import axios from '@fs/zion-axios'

import Cache from '@fs/zion-cache'

const defaultCache = new Cache({ storeName: 'memoriesCache', type: 'session' })
const artifactUrl = '/service/memories/manager/artifacts/'

export const getMemory = async (memoryId, useCache = true) => {
  const cacheKey = `memory-${memoryId}`

  if (useCache && defaultCache) {
    const cachedData = await defaultCache.getItem(cacheKey)
    if (cachedData === '__404__') return null
    if (cachedData !== null) return cachedData
  }

  try {
    const { data } = await axios.get(`${artifactUrl}${memoryId}`)

    if (useCache && defaultCache) {
      await defaultCache.setItem(cacheKey, data)
    }

    return data
  } catch (error) {
    console.error('Error fetching memory or image data:', error)

    if (error.response?.status === 404) {
      if (useCache && defaultCache) {
        await defaultCache.setItem(cacheKey, '__404__', { ttl: 300 }) // Expires after 5 minutes
      }
      return null
    }
    throw error
  }
}

export const setMemoryCache = (memoryId, updatedMemory, cache = defaultCache) => {
  const cacheKey = `memory-${memoryId}`

  cache.setItem(cacheKey, updatedMemory)
}

export const clearMemoryCache = (memoryId, cache = defaultCache) => {
  const cacheKey = `memory-${memoryId}`
  // The Zion Cache wrapper does not expose removeItem all the way like setItem or updateItem.  We have to drill down one more layer
  if (cache) {
    cache.localforage.removeItem(cacheKey)
  }
}

export const getStoriesFromCisId = (cisId) => {
  if (!cisId) throw new Error('No cisId passed to getStoriesFromCisId')
  return axios.get(`/service/memories/presentation/patrons/${cisId}/artifacts?artifactCategory=TEXT`)
}

export const updateStory = async (memoryId, text, title) => {
  const response = await axios.put(`${artifactUrl}${memoryId}/files?title=${encodeURIComponent(title)}`, text, {
    headers: { 'Content-Type': 'text/plain' },
  })

  return response.data.artifact
}

export const uploadStory = ({ name, file }) =>
  axios.post(
    `${artifactUrl}files?filename=story.txt&title=${encodeURIComponent(name)}&isPrivate=true&artifactCategory=TEXT`,
    file,
    {
      headers: { 'Content-Type': 'text/plain' },
    }
  )
// New function to handle story upload
export const createStory = async ({ text, title }) => {
  try {
    const fileBlob = new Blob([text], { type: 'text/plain' }) // Convert to Blob
    const response = await uploadStory({ name: title, file: fileBlob })

    return response.data.artifact
  } catch (error) {
    console.error('Error uploading story:', error)
    throw error
  }
}

export const handleFetchText = async (url, isUpdating = false) => {
  if (!url) {
    throw new Error('No URL provided')
  }
  // Add cache-busting param --- If we don't do this, the story will not update due to the cache
  let fetchUrl = url
  if (isUpdating) {
    const separator = url.includes('?') ? '&' : '?'
    fetchUrl = `${url}${separator}_cb=${Date.now()}`
  }
  try {
    const response = await axios.get(fetchUrl)

    if (typeof response.data === 'string') {
      return response.data
    }

    if (typeof response.data === 'object' && response.data !== null) {
      return response.data.description || response.data.title || JSON.stringify(response.data)
    }

    return String(response.data)
  } catch (error) {
    console.error('Error fetching text:', error)
    throw error
  }
}

const pollRotateProcess = async (memoryId, maxRetries = 15, delay = 500) => {
  const rotatePollDelay = () =>
    new Promise((resolve) => {
      setTimeout(resolve, delay)
    })

  const poll = async (attempt) => {
    if (attempt >= maxRetries) {
      throw new Error('Rotation processing timed out')
    }

    const memory = await getMemory(memoryId, false)
    if (memory?.imageProcessingState === 'PROCESSED') {
      return memory
    }

    await rotatePollDelay()
    return poll(attempt + 1)
  }

  return poll(0)
}

export const handleRotate = async ({ memoryId, rotationDegrees }) => {
  await axios.post(`${artifactUrl}${memoryId}/transform?relativeRotation=${rotationDegrees}`)
  const updatedMemory = await pollRotateProcess(memoryId)

  return updatedMemory
}

const linkDuplicateImage = async ({ memoryId, name, pid }) => {
  const { data: duplicateMemory } = await axios.get(`${artifactUrl}${memoryId}`)

  try {
    await axios.post(`/service/memories/manager/personas?treePersonId=${pid}`, {
      memoryId: duplicateMemory.id,
      name,
    })
  } catch (error) {
    if (error.response.status !== 409) throw error
  }

  return { artifact: duplicateMemory, success: true }
}

export const uploadPhoto = async ({ file, pid, name }) => {
  if (!file || !pid || !name) {
    console.error('Cannot upload photo with undefined data', { file, pid, name })
    return null
  }

  try {
    const { data } = await axios.post(
      `${artifactUrl}files?filename=${encodeURIComponent(name)}&title=${encodeURIComponent(name)}&isPrivate=true`,
      file,
      { headers: { 'Content-Type': file.type } }
    )
    return { artifact: data.artifact, success: true }
  } catch (error) {
    if (error.response.status === 409) {
      const memoryId = error.response.headers.warning.split('(')[1].slice(0, -1)
      return linkDuplicateImage({ memoryId, name, pid })
    }
    throw error
  }
}
